import Leaflet from 'leaflet';
import 'leaflet.markercluster';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';

import { forwardClusterEvent, GroupedPinContext } from '../clusterEvents';
import { markerOpacity, UNASSIGNED_CLUSTER_RADIUS_PX, UNASSIGNED_PIN } from '../mapMarkerStyles';
import { MapUnassignedMarkerVM } from '../mapVM';
import { buildUnassignedMarkerEventHandlers } from '../markerEvents';
import { UnassignedTooltipVM } from '../unassignedTooltip';
import { buildUnassignedTooltipNode } from '../unassignedTooltipNode';

type Props = {
  markers: readonly MapUnassignedMarkerVM[];
  /** ホバーに出す内容（地点の id 引き）。引けない地点にはホバーを付けない */
  tooltips?: ReadonlyMap<string, UnassignedTooltipVM>;
  /** 何か選ばれているか。選択が無いうちは全部濃いまま出す */
  hasSelection: boolean;
  selectedUnassignedIds?: ReadonlySet<string>;
  /** context は集合状態（クラスター / 同一地点に複数案件）のときだけ渡る */
  onUnassignedClick?: (id: string, context?: GroupedPinContext) => void;
  /** 右クリックで選択解除（旧地図と同じ操作） */
  onUnassignedDeselect?: (id: string) => void;
};

// 旧地図と同じ PNG ピン。自前の 14px の丸だと、クラスターが解けた後に
// どこにあるか分からないという指摘があった（寸法は mapMarkerStyles が正本）。
// 1 つの Icon を全マーカーで使い回す（leaflet は描画のたびに DOM を作るので共有できる）
const unassignedIcon = new Leaflet.Icon({
  iconUrl: UNASSIGNED_PIN.iconUrl,
  iconSize: [...UNASSIGNED_PIN.iconSize],
  iconAnchor: [...UNASSIGNED_PIN.iconAnchor],
  tooltipAnchor: [...UNASSIGNED_PIN.tooltipAnchor],
  className: 'map-unassigned-pin',
});

const TOOLTIP_OPTIONS: Leaflet.TooltipOptions = {
  direction: 'top',
  offset: [0, -12],
  opacity: 1,
  className: 'unassigned-hover-tooltip',
};

type PinHandlers = ReturnType<typeof buildUnassignedMarkerEventHandlers>;

/**
 * クラスターからイベントを流すときに読まれる置き場所（clusterEvents の
 * `marker.options.eventHandlers`）。leaflet の型には無いのでここで名前を付ける。
 */
const handlerSlotOf = (marker: Leaflet.Marker): { eventHandlers?: PinHandlers } =>
  marker.options as { eventHandlers?: PinHandlers };

/** 1 本のピンについて、いま反映済みの内容。差分判定に使う */
type ManagedPin = {
  marker: Leaflet.Marker;
  vm: MapUnassignedMarkerVM;
  opacity: number;
  tooltip: UnassignedTooltipVM | undefined;
};

function applyTooltip(marker: Leaflet.Marker, vm: UnassignedTooltipVM | undefined): void {
  if (!vm) {
    marker.unbindTooltip();
    return;
  }
  const node = buildUnassignedTooltipNode(vm);
  if (marker.getTooltip()) marker.setTooltipContent(node);
  else marker.bindTooltip(node, TOOLTIP_OPTIONS);
}

/**
 * 未割当ピンのクラスター描画。
 *
 * Why not 宣言的な `<Marker>`: react-leaflet の `useLayerLifecycle`
 * (@react-leaflet/core/lib/layer.js) はマーカー 1 本ずつ `addLayer` / `removeLayer` を
 * 呼ぶ。leaflet.markercluster の `addLayer` は 1 本ごとに `_recalculateBounds` と
 * `_refreshClustersIcons` を回すので、地点が増えると表示フィルタの切り替えで
 * メインスレッドが止まる（logposeTech/log_web_issues#321）。`addLayers` / `removeLayers`
 * ならこの再計算は 1 回で済む。
 *
 * もう 1 つ、宣言的なままだと消せない負荷がある。`position` は
 * `buildMapUnassignedMarkers` が再計算のたびに新しい配列で作るため参照が変わり、
 * react-leaflet が `setLatLng` を呼ぶ。markercluster は子の `move` を拾って
 * `_moveChild` = `removeLayer` + `addLayer` を実行する（leaflet.markercluster-src.js
 * 716-734）ので、**中身が変わっていないピンまで**重い経路を 2 回通っていた。
 * ここでは緯度経度の数値が変わったときだけ動かす。
 *
 * Why not 共有 `src/components/MarkerClusterGroup.tsx` を直す: 旧地図 `/plans` の
 * `PlanningMapOrderMarkers.presenter.tsx` も同じものを使っており、そちらまで
 * 描画方式が変わってしまう。
 */
const UnassignedMarkerCluster = ({
  markers,
  tooltips,
  hasSelection,
  selectedUnassignedIds,
  onUnassignedClick,
  onUnassignedDeselect,
}: Props) => {
  const map = useMap();
  const groupRef = useRef<Leaflet.MarkerClusterGroup | null>(null);
  const pinsRef = useRef(new Map<string, ManagedPin>());

  // 呼び出し側のハンドラは毎レンダー別の関数になる。leaflet へ直接張ると
  // 地点数ぶんの off/on が再描画のたびに走るので、ref 越しに最新を読む
  const handlersRef = useRef({ onUnassignedClick, onUnassignedDeselect });
  handlersRef.current = { onUnassignedClick, onUnassignedDeselect };

  useEffect(() => {
    /* クラスター設定は旧地図 (PlanningMapOrderMarkers.presenter.tsx:75-82) に合わせる。
       既定のままだとズームインしても個別のピンにばらけない。
       zoomToBoundsOnClick を切り、クラスターのクリック / 右クリックを
       配下マーカーの click / contextmenu へ流す（＝右リストの選択と連動する）。

       Why not 花びら展開 (spiderfy): クラスターを押すと足の生えた花びら状に散らばるが、
       押した点との対応が読めず「辞めたい」という指摘があった (log_web_issues#2430)。
       zoomToBoundsOnClick と両方 false にすると leaflet.markercluster は
       _zoomOrSpiderfy のリスナー自体を張らない (leaflet.markercluster-src.js:856)。
       クラスター選択 (forwardClusterEvent) は別リスナーなので残る。

       Why not chunkedLoading: 分割追加は setTimeout をまたぐため、分割の合間に
       削除が来ると、まだ入っていないマーカーは removeLayers に拾われず
       (__parent が無いので非ポイント側へ流れる) 後から追加されて消えないピンになる。
       分割が始まるのは 1 回の追加が chunkInterval (既定 200ms) を超えたときだけで、
       addLayers 経由なら 2,000 地点でも遠く及ばない。踏めない速さのために
       消えないピンのリスクを持つ必要がない。 */
    const group = new Leaflet.MarkerClusterGroup({
      animate: false,
      animateAddingMarkers: false,
      maxClusterRadius: UNASSIGNED_CLUSTER_RADIUS_PX,
      zoomToBoundsOnClick: false,
      spiderfyOnMaxZoom: false,
    });
    group.on('clusterclick', forwardClusterEvent('click'));
    group.on('clustercontextmenu', forwardClusterEvent('contextmenu'));
    map.addLayer(group);
    groupRef.current = group;

    const pins = pinsRef.current;
    return () => {
      map.removeLayer(group);
      groupRef.current = null;
      pins.clear();
    };
  }, [map]);

  useEffect(() => {
    const group = groupRef.current;
    if (!group) return;
    const pins = pinsRef.current;

    const buildHandlers = (vm: MapUnassignedMarkerVM): PinHandlers =>
      buildUnassignedMarkerEventHandlers(vm.entries, {
        onUnassignedClick: (id, context) => handlersRef.current.onUnassignedClick?.(id, context),
        onUnassignedDeselect: (id) => handlersRef.current.onUnassignedDeselect?.(id),
      });

    const nextIds = new Set(markers.map((m) => m.id));
    const removed: Leaflet.Marker[] = [];
    pins.forEach((pin, id) => {
      if (nextIds.has(id)) return;
      removed.push(pin.marker);
      pins.delete(id);
    });
    if (removed.length > 0) group.removeLayers(removed);

    const added: Leaflet.Marker[] = [];
    markers.forEach((vm) => {
      const isSelected = vm.entries.some(({ id }) => selectedUnassignedIds?.has(id) ?? false);
      const opacity = markerOpacity({ hasSelection, isSelected });
      const tooltip = tooltips?.get(vm.id);
      const pin = pins.get(vm.id);

      if (pin) {
        if (pin.vm !== vm) {
          handlerSlotOf(pin.marker).eventHandlers = buildHandlers(vm);
          const at = pin.marker.getLatLng();
          // 地点 id は座標キー由来なので普段は動かない。数値が変わったときだけ動かす
          // （呼ぶと markercluster が remove + add をやり直す）
          if (at.lat !== vm.position[0] || at.lng !== vm.position[1]) pin.marker.setLatLng(vm.position);
          pin.vm = vm;
        }
        if (pin.opacity !== opacity) {
          pin.marker.setOpacity(opacity);
          pin.opacity = opacity;
        }
        if (pin.tooltip !== tooltip) {
          applyTooltip(pin.marker, tooltip);
          pin.tooltip = tooltip;
        }
        return;
      }

      const marker = Leaflet.marker(vm.position, { icon: unassignedIcon, opacity });
      handlerSlotOf(marker).eventHandlers = buildHandlers(vm);
      // 読む先を options.eventHandlers に寄せる（クラスター経由と同じ場所）。
      // こうしておくと、ハンドラが差し替わってもリスナを張り直さずに済む
      marker.on('click', () => handlerSlotOf(marker).eventHandlers?.click());
      marker.on('contextmenu', () => handlerSlotOf(marker).eventHandlers?.contextmenu());
      applyTooltip(marker, tooltip);
      pins.set(vm.id, { marker, vm, opacity, tooltip });
      added.push(marker);
    });
    if (added.length > 0) group.addLayers(added);
  }, [map, markers, tooltips, hasSelection, selectedUnassignedIds]);

  return null;
};

export default UnassignedMarkerCluster;
